/**
 * Shared Flow Utilities for Warmup and Retargeting Flows
 * 
 * This module provides reusable functions for managing flow state on contacts/leads.
 * - Warmup Flows: Use 'warmupFlow' field on contact documents (WorldDb/Targets)
 * - Retargeting Flows: Use 'activeFlow' field on lead documents (Leads)
 */

import { doc, updateDoc, getDoc, runTransaction, deleteDoc } from 'firebase/firestore';
import { ref, get, set, remove } from 'firebase/database';
import { firestore, database } from '../firebaseConfig';
import { logSalesAnalytics } from './analyticsTracker';
import { computeHasEmail } from '../shared/enums';
import { EMAIL_ACTION_TYPES } from '../shared/flowTouchpoints';
import { getFlowSenderConfig, resolveFlowSenderAccountId } from './flowSenderUtils';
import { collection, getDocs } from 'firebase/firestore';

// Field names for different flow types
const FLOW_FIELD = {
  warmup: 'warmupFlow',
  retargeting: 'activeFlow'
};

const HISTORY_FIELD = {
  warmup: 'warmupFlowHistory',
  retargeting: 'flowHistory'
};

/**
 * Helper: Add completed flow to history
 */
const addFlowToHistory = (history = [], flowState, flowDefinition, reason, timestamp) => {
  const historyEntry = {
    flowId: flowState.flowId,
    flowName: flowState.flowName || flowDefinition?.name || 'Unknown Flow',
    startedAt: flowState.startedAt,
    endedAt: timestamp,
    reason: reason, // 'completed', 'stopped', 'replaced'
    lastStepIndex: flowState.currentStepIndex,
    stepHistory: flowState.stepHistory || {}
  };
  return [...history, historyEntry];
};

// Automated node types that require backend processing
const AUTO_NODE_TYPES = ['auto_send_email', 'auto_check_reply'];

// A manual "Send Email" step (action / email_sent) that opted into having the
// backend pre-generate the email ("Prepare in backend"). It is enqueued like an
// automated step so the flow-automation scheduler generates + stores the email
// as soon as the step becomes due — but the flow does NOT auto-advance; the
// user reviews and sends it later from the World DB "Prepared" tab.
const isPrepareEmailStep = (step) =>
  !!step &&
  step.nodeType === 'action' &&
  EMAIL_ACTION_TYPES.includes(step.type) &&
  step.prepareInBackend === true;

// A step the backend queue must handle: fully-automated nodes OR a manual
// send-email step flagged for backend preparation.
const needsBackendQueue = (step) =>
  !!step && (AUTO_NODE_TYPES.includes(step.nodeType) || isPrepareEmailStep(step));

// A LinkedIn manual touchpoint (any action step whose type mentions linkedin).
// Used to auto-skip the whole channel once a contact is flagged as
// LinkedIn-inactive (no reachable target).
const isLinkedInActionStep = (step) => {
  if (!step) return false;
  const t = (step.type || step.nodeType || '').toLowerCase();
  return t.includes('linkedin');
};

/**
 * Helper: Write an entry to the automation queue if the next step is an automated node.
 * Fire-and-forget — errors are logged but never break the flow.
 */
const enqueueAutomationIfNeeded = async (userId, contactId, flow, stepIndex, flowState, flowType = 'warmup') => {
  try {
    const step = flow.steps?.[stepIndex];
    if (!needsBackendQueue(step)) return;

    const now = Date.now();
    const executeAt = flowState?.nextStepAvailableAt || now;
    const prepareStep = isPrepareEmailStep(step);

    const queueEntry = {
      userId,
      flowId: flowState.flowId,
      flowName: flowState.flowName || flow.name || '',
      flowType,
      stepIndex,
      // Manual send-email prepare steps are queued under a dedicated stepType so
      // the scheduler generates + stores (never sends) and never advances.
      stepType: prepareStep ? 'prepare_email' : step.nodeType,
      executeAt,
      retryCount: 0,
      createdAt: now,
    };

    if (prepareStep) {
      queueEntry.templateId = step.templateId || '';
      queueEntry.templateName = step.templateName || '';
      queueEntry.senderId = (step.senderMode === 'fixed' && step.senderId) ? step.senderId : 'random';
    }
    if (step.nodeType === 'auto_send_email') {
      queueEntry.templateId = step.templateId || '';
      queueEntry.templateName = step.templateName || '';
      queueEntry.senderId = step.senderId || 'random';
    }
    if (step.nodeType === 'auto_check_reply') {
      queueEntry.timeoutDays = step.timeoutDays || 3;
      queueEntry.checkStartedAt = now;
    }

    const queueRef = ref(database, `automationQueue/${userId}/${contactId}`);
    await set(queueRef, queueEntry);
  } catch (err) {
    console.error('[Automation Queue] Failed to enqueue:', err);
  }
};

/**
 * Helper: Delete a contact's pre-prepared flow email (if any). A backend
 * onDelete trigger resets the contact's `has_prepared_email` flag, so callers
 * only need to remove the doc. Fire-and-forget — never breaks the flow.
 * Used when a prepared email is superseded (flow left the step, stopped,
 * completed or the email was actually sent).
 */
export const clearPreparedEmail = async (userId, contactId) => {
  if (!userId || !contactId) return;
  try {
    await deleteDoc(doc(firestore, `World/${userId}/preparedEmails/${contactId}`));
  } catch (err) {
    // A missing doc is fine; log anything else but never break the flow.
    console.warn('[Prepared Email] Failed to clear prepared email:', err?.message || err);
  }
};

/**
 * Helper: Remove a contact from the automation queue.
 */
const dequeueAutomation = async (userId, contactId) => {
  try {
    const queueRef = ref(database, `automationQueue/${userId}/${contactId}`);
    await remove(queueRef);
  } catch (err) {
    console.error('[Automation Queue] Failed to dequeue:', err);
  }
};

/**
 * Helper: Find the next node based on edges (for visual flows with branching)
 */
const findNextNodeFromEdges = (flow, currentNodeId, sourceHandle = null) => {
  if (!flow.visualData?.edges) return null;
  
  const outgoingEdges = flow.visualData.edges.filter(edge => {
    if (edge.source !== currentNodeId) return false;
    if (sourceHandle && edge.sourceHandle !== sourceHandle) return false;
    return true;
  });
  
  if (outgoingEdges.length === 0) return null;
  
  const targetNodeId = outgoingEdges[0].target;
  const stepIndex = flow.steps.findIndex(step => step.id === targetNodeId);
  
  return stepIndex >= 0 ? stepIndex : null;
};

/**
 * Start a flow for a contact
 * 
 * @param {string} userId - The user ID (owner of the World)
 * @param {string} contactId - The contact document ID
 * @param {object} flow - The flow definition object
 * @param {string} flowType - 'warmup' or 'retargeting'
 * @returns {Promise<boolean>} Success status
 */
export const startFlowForContact = async (userId, contactId, flow, flowType = 'warmup') => {
  if (!userId || !contactId || !flow || !flow.steps || flow.steps.length === 0) {
    console.error('startFlowForContact: Missing required parameters');
    return false;
  }

  const flowField = FLOW_FIELD[flowType];
  const historyField = HISTORY_FIELD[flowType];

  try {
    const now = Date.now();
    const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
    
    // Get current contact data to check for existing flow
    const contactSnap = await getDoc(contactRef);
    const contactData = contactSnap.data() || {};
    
    // If there's an existing flow, add it to history
    let updatedHistory = contactData[historyField] || [];
    if (contactData[flowField] && contactData[flowField].currentStepIndex >= 0) {
      updatedHistory = addFlowToHistory(
        updatedHistory, 
        contactData[flowField], 
        null, 
        'replaced', 
        now
      );
    }
    
    // Calculate when the first step is due
    const firstStepDelay = parseInt(flow.steps[0]?.delayDays) || 0;
    const firstStepAvailableAt = now + (firstStepDelay * 24 * 60 * 60 * 1000);
    
    const newFlowState = {
      flowId: flow.id,
      flowName: flow.name,
      startedAt: now,
      currentStepIndex: 0,
      stepHistory: {},
      nextStepAvailableAt: firstStepAvailableAt,
    };

    // Hybrid sender rotation: when the flow's Send-Email step uses a
    // random/choose sender, pre-assign a specific account here (via the
    // persistent round-robin counter) so bulk-assigning many contacts spreads
    // evenly. Send-time resolution backfills this for flows started earlier.
    try {
      const senderConfig = getFlowSenderConfig(flow);
      if (senderConfig && (senderConfig.senderMode === 'random' || senderConfig.senderMode === 'choose')) {
        const accountsSnap = await getDocs(collection(firestore, `world_users/${userId}/emailAccounts`));
        const accountIds = accountsSnap.docs.map(d => d.id);
        const assignedId = await resolveFlowSenderAccountId(userId, flow.id, senderConfig, accountIds);
        if (assignedId) newFlowState.flowSenderId = assignedId;
      }
    } catch (err) {
      console.error('[Flow] Sender pre-assignment failed:', err);
    }

    await updateDoc(contactRef, {
      [flowField]: newFlowState,
      [historyField]: updatedHistory,
      updated_at: now,
      flow_sort_priority: 2,
    });

    // Fire-and-forget: enqueue first step for backend if automated or delay
    try {
      const firstStep = flow.steps[0];
      if (needsBackendQueue(firstStep)) {
        enqueueAutomationIfNeeded(userId, contactId, flow, 0, newFlowState, flowType);
      } else if (firstStep && firstStep.nodeType === 'delay' && (parseInt(firstStep.delayDays) || 0) > 0) {
        const queueRef = ref(database, `automationQueue/${userId}/${contactId}`);
        set(queueRef, {
          userId, flowId: flow.id, flowName: flow.name || '', flowType,
          stepIndex: 0, stepType: 'resolve_delay',
          executeAt: firstStepAvailableAt, retryCount: 0, createdAt: now,
        });
      }
    } catch (_) { /* never break flow start */ }

    return true;
  } catch (error) {
    console.error('Error starting flow:', error);
    return false;
  }
};

/**
 * Complete a flow step for a contact
 * 
 * @param {string} userId - The user ID
 * @param {string} contactId - The contact document ID
 * @param {object} flow - The flow definition with steps
 * @param {number} stepIndex - The step index to complete
 * @param {string} flowType - 'warmup' or 'retargeting'
 * @param {string} decision - Optional decision for condition nodes ('yes' or 'no')
 * @returns {Promise<{success: boolean, isComplete: boolean}>}
 */
export const completeFlowStep = async (userId, contactId, flow, stepIndex, flowType = 'warmup', decision = null, { force = false } = {}) => {
  if (!userId || !contactId || !flow) {
    console.error('completeFlowStep: Missing required parameters');
    return { success: false, isComplete: false };
  }

  const flowField = FLOW_FIELD[flowType];
  const historyField = HISTORY_FIELD[flowType];

  try {
    const now = Date.now();
    const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);

    // Use a Firestore transaction to prevent race conditions
    const result = await runTransaction(firestore, async (transaction) => {
      const contactSnap = await transaction.get(contactRef);
      const contactData = contactSnap.data() || {};

      // Opt-out check: if contact has opted out, stop the flow
      if (contactData.globalOptOut === true) {
        throw new Error('Contact has opted out — flow step blocked');
      }

      const currentFlowState = contactData[flowField];

      if (!currentFlowState) {
        throw new Error('No active flow found for contact');
      }

      // Verify we're completing the expected step (guard against stale UI)
      if (currentFlowState.currentStepIndex !== stepIndex) {
        throw new Error(`Step mismatch: expected ${stepIndex}, contact is on ${currentFlowState.currentStepIndex}`);
      }

      // Check if step delay has passed (skip when force=true, e.g. email was manually sent)
      const stepAvailableAt = currentFlowState.nextStepAvailableAt || 0;
      if (!force && now < stepAvailableAt) {
        throw new Error('Step not yet available');
      }

      const currentStep = flow.steps[stepIndex];
      if (!currentStep) {
        throw new Error('Step not found in flow');
      }

      // Update step history
      const stepHistory = {
        ...(currentFlowState.stepHistory || {}),
        [stepIndex]: {
          completedAt: now,
          ...(decision ? { decision } : {})
        }
      };

      // Determine next step
      let nextStepIndex = null;
      let isFlowComplete = false;
      let endType = null;

      // Check if this is an END node
      if (currentStep.nodeType === 'end' || currentStep.type === 'end') {
        isFlowComplete = true;
        nextStepIndex = -1;
        endType = currentStep.endType;
        if (!endType && flow.visualData?.nodes) {
          const visualNode = flow.visualData.nodes.find(n => n.id === currentStep.id);
          endType = visualNode?.data?.endType;
        }
        endType = endType || 'completed';
      } else if (currentStep.nodeType === 'split') {
        // A/B Split: randomly choose path
        const splitA = currentStep.splitA || 50;
        const random = Math.random() * 100;
        const splitChoice = random < splitA ? 'a' : 'b';
        nextStepIndex = findNextNodeFromEdges(flow, currentStep.id, splitChoice);
        stepHistory[stepIndex].splitChoice = splitChoice;
      } else if (currentStep.nodeType === 'condition' && decision) {
        // Condition node with decision
        nextStepIndex = findNextNodeFromEdges(flow, currentStep.id, decision);
      } else if (currentStep.nodeType === 'auto_check_reply' && decision) {
        // Check-Reply node: MUST follow the edge matching the actual decision
        // ('yes'/'no'). Without this branch it fell through to the generic
        // "single outgoing edge" case below, which ignores sourceHandle and
        // grabs whichever edge happens to be first in the array — silently
        // taking the wrong branch (e.g. "yes"→End) regardless of whether
        // there really was a reply. Mirrors the backend's advanceFlow().
        nextStepIndex = findNextNodeFromEdges(flow, currentStep.id, decision);
      } else {
        // Regular node: follow the single outgoing edge
        nextStepIndex = findNextNodeFromEdges(flow, currentStep.id);
      }

      // Fallback to sequential if no edge found
      if (!isFlowComplete && nextStepIndex === null) {
        nextStepIndex = stepIndex + 1;
      }

      // Check if we've run out of steps
      if (!isFlowComplete) {
        isFlowComplete = nextStepIndex >= (flow.steps?.length || 0) || nextStepIndex < 0;
      }

      // Auto-complete non-interactive nodes (tag, delay with 0 days) until we reach an end node or interactive node
      let autoCompleteCount = 0;
      const maxAutoComplete = 10; // Safety limit
      const autoAppliedTags = {};

      while (!isFlowComplete && flow.steps[nextStepIndex] && autoCompleteCount < maxAutoComplete) {
        const nextStep = flow.steps[nextStepIndex];

        // Check if this is an END node - complete the flow
        if (nextStep.nodeType === 'end' || nextStep.type === 'end') {
          isFlowComplete = true;
          endType = nextStep.endType;
          if (!endType && flow.visualData?.nodes) {
            const visualNode = flow.visualData.nodes.find(n => n.id === nextStep.id);
            endType = visualNode?.data?.endType;
          }
          endType = endType || 'completed';
          stepHistory[nextStepIndex] = { completedAt: now, autoCompleted: true };
          break;
        }

        // Check if this is a non-interactive node that should be auto-completed
        const isNonInteractive =
          nextStep.nodeType === 'tag' ||
          (nextStep.nodeType === 'delay' && (parseInt(nextStep.delayDays) || 0) === 0);

        // Check if this is an auto-evaluable condition
        const isAutoCondition = nextStep.nodeType === 'condition' && nextStep.condition &&
          autoEvaluateCondition(nextStep.condition, contactData) !== null;

        // Skip LinkedIn touchpoints when the contact has been marked as
        // LinkedIn-inactive — the whole channel is dead, so any remaining
        // LinkedIn steps in the flow are auto-completed and passed over.
        const isSkippableLinkedIn =
          contactData.lead_linkedin_inactive === true && isLinkedInActionStep(nextStep);

        if (!isNonInteractive && !isAutoCondition && !isSkippableLinkedIn) {
          break;
        }

        // Auto-complete this non-interactive node
        stepHistory[nextStepIndex] = {
          completedAt: now,
          autoCompleted: true,
          ...(isSkippableLinkedIn ? { skippedReason: 'linkedin_inactive' } : {}),
        };

        let followingStepIndex;
        if (isAutoCondition) {
          // Auto-evaluate condition and follow the appropriate path
          const autoDecision = autoEvaluateCondition(nextStep.condition, contactData);
          stepHistory[nextStepIndex].decision = autoDecision;
          stepHistory[nextStepIndex].autoEvaluated = true;
          followingStepIndex = findNextNodeFromEdges(flow, nextStep.id, autoDecision);
        } else {
          // Apply tags from tag nodes to the contact
          if (nextStep.nodeType === 'tag' && nextStep.tags && Array.isArray(nextStep.tags)) {
            nextStep.tags.forEach(tag => {
              const tagKey = tag.id || tag.name.toLowerCase().replace(/\s+/g, '_');
              autoAppliedTags[tagKey] = {
                id: tag.id, name: tag.name, color: tag.color || '#6b7280',
                icon: tag.icon || 'fa-tag', addedAt: now, addedBy: 'flow',
                flowId: currentFlowState.flowId, flowName: currentFlowState.flowName || flow.name,
              };
            });
          }
          followingStepIndex = findNextNodeFromEdges(flow, nextStep.id);
        }

        if (followingStepIndex === null || followingStepIndex < 0) {
          isFlowComplete = true;
          break;
        }

        nextStepIndex = followingStepIndex;
        autoCompleteCount++;
      }

      // Calculate when the next step is available
      let nextStepAvailableAt = null;
      if (!isFlowComplete && flow.steps[nextStepIndex]) {
        const nextStepDelay = parseInt(flow.steps[nextStepIndex].delayDays) || 0;
        nextStepAvailableAt = now + (nextStepDelay * 24 * 60 * 60 * 1000);
      }

      // Build update payload
      let updatePayload = {};
      let updatedHistory = contactData[historyField] || [];

      // Check if auto-applied tags include any negative/strikethrough tags
      const NEGATIVE_TAG_IDS = ['not_qualified', 'not_interested', 'competitor', 'unresponsive'];
      const hasAutoAppliedNegativeTag = Object.keys(autoAppliedTags).some(
        tagKey => NEGATIVE_TAG_IDS.includes(tagKey)
      );

      if (isFlowComplete) {
        let flowStatus = endType || 'completed';

        // Override to 'not_interested' if flow ended as 'completed' but a negative tag was applied
        if (flowStatus === 'completed' && hasAutoAppliedNegativeTag) {
          flowStatus = 'not_interested';
        }

        const isDisqualified = ['disqualified', 'not_interested', 'wrong_contact'].includes(flowStatus);

        updatedHistory = addFlowToHistory(
          updatedHistory,
          { ...currentFlowState, stepHistory, endType: flowStatus },
          flow,
          flowStatus,
          now
        );

        // Build a "flow completed" tag so we can easily identify contacts that finished a flow
        const flowName = currentFlowState.flowName || flow?.name || 'Unknown Flow';
        const flowCompletedTagKey = `flow_completed_${currentFlowState.flowId}`;
        const flowCompletedTag = {
          id: flowCompletedTagKey,
          name: `✓ ${flowName}`,
          color: isDisqualified ? '#ef4444' : '#10b981',
          icon: isDisqualified ? 'fa-times-circle' : 'fa-check-circle',
          addedAt: now,
          addedBy: 'flow_engine',
          flowId: currentFlowState.flowId,
          flowName: flowName,
          endType: flowStatus,
        };

        updatePayload = {
          [flowField]: null,
          [historyField]: updatedHistory,
          flowEndStatus: flowStatus,
          flowEndedAt: now,
          updated_at: now,
          flow_sort_priority: 4,
          [`tags.${flowCompletedTagKey}`]: flowCompletedTag,
        };

        if (isDisqualified) {
          updatePayload.isDisqualified = true;
          updatePayload.disqualifiedAt = now;
          updatePayload.disqualifiedReason = flowStatus;
          updatePayload.lead_contacted = 'Not Interested';
        }
      } else {
        updatePayload = {
          [flowField]: {
            ...currentFlowState,
            currentStepIndex: nextStepIndex,
            stepHistory,
            nextStepAvailableAt,
          },
          updated_at: now,
          flow_sort_priority: 2,
        };
      }

      // Write auto-applied tags from tag nodes to the contact
      for (const [tagKey, tagData] of Object.entries(autoAppliedTags)) {
        updatePayload[`tags.${tagKey}`] = tagData;
      }

      transaction.update(contactRef, updatePayload);

      const finalFlowEndStatus = isFlowComplete
        ? (updatePayload.flowEndStatus || endType || 'completed')
        : null;

      return {
        success: true,
        isComplete: isFlowComplete,
        _stepType: currentStep.type || currentStep.nodeType,
        newFlowState: isFlowComplete ? null : updatePayload[flowField],
        flowEndStatus: finalFlowEndStatus,
        autoAppliedTags: Object.keys(autoAppliedTags).length > 0 ? autoAppliedTags : null,
      };
    });

    // Fire-and-forget: anonymized sales analytics for the completed flow step
    try {
      if (result.success && result._stepType) {
        logSalesAnalytics(userId, contactId, { touchpointType: result._stepType });
      }
    } catch (_) { /* never break existing flow */ }

    // Fire-and-forget: a pre-prepared email for THIS step is now stale once the
    // step is completed (sent / marked done / auto-advanced) — clear it so it
    // no longer shows in the "Prepared" tab.
    try {
      if (result.success && isPrepareEmailStep(flow.steps?.[stepIndex])) {
        clearPreparedEmail(userId, contactId);
      }
    } catch (_) { /* never break existing flow */ }

    // Fire-and-forget: automation queue management
    try {
      if (result.success) {
        if (result.isComplete) {
          dequeueAutomation(userId, contactId);
          clearPreparedEmail(userId, contactId);
        } else if (result.newFlowState) {
          const nextStep = flow.steps?.[result.newFlowState.currentStepIndex];
          if (needsBackendQueue(nextStep)) {
            enqueueAutomationIfNeeded(userId, contactId, flow, result.newFlowState.currentStepIndex, result.newFlowState, flowType);
          } else if (nextStep && nextStep.nodeType === 'delay' && (parseInt(nextStep.delayDays) || 0) > 0) {
            // Enqueue delay resolution so the scheduler advances it even if user is offline
            const queueRef = ref(database, `automationQueue/${userId}/${contactId}`);
            await set(queueRef, {
              userId,
              flowId: result.newFlowState.flowId,
              flowName: result.newFlowState.flowName || flow.name || '',
              flowType,
              stepIndex: result.newFlowState.currentStepIndex,
              stepType: 'resolve_delay',
              executeAt: result.newFlowState.nextStepAvailableAt || Date.now(),
              retryCount: 0,
              createdAt: Date.now(),
            });
          } else {
            dequeueAutomation(userId, contactId);
          }
        }
      }
    } catch (_) { /* never break existing flow */ }

    return result;
  } catch (error) {
    console.error('Error completing flow step:', error);
    return { success: false, isComplete: false };
  }
};

/**
 * Stop a flow for a contact
 * 
 * @param {string} userId - The user ID
 * @param {string} contactId - The contact document ID
 * @param {object} flow - The flow definition (optional, for history)
 * @param {string} flowType - 'warmup' or 'retargeting'
 * @returns {Promise<boolean>}
 */
export const stopFlowForContact = async (userId, contactId, flow = null, flowType = 'warmup') => {
  if (!userId || !contactId) {
    console.error('stopFlowForContact: Missing required parameters');
    return false;
  }

  const flowField = FLOW_FIELD[flowType];
  const historyField = HISTORY_FIELD[flowType];

  try {
    const now = Date.now();
    const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
    
    // Get current contact data
    const contactSnap = await getDoc(contactRef);
    const contactData = contactSnap.data() || {};
    const currentFlowState = contactData[flowField];
    
    if (!currentFlowState) {
      // No flow to stop
      return true;
    }

    // Add to history as 'stopped'
    let updatedHistory = contactData[historyField] || [];
    updatedHistory = addFlowToHistory(
      updatedHistory,
      currentFlowState,
      flow,
      'stopped',
      now
    );

    // Build a "flow stopped" tag
    const flowName = currentFlowState.flowName || flow?.name || 'Unknown Flow';
    const flowCompletedTagKey = `flow_completed_${currentFlowState.flowId}`;
    const flowCompletedTag = {
      id: flowCompletedTagKey,
      name: `⏹ ${flowName}`,
      color: '#f59e0b',
      icon: 'fa-stop-circle',
      addedAt: now,
      addedBy: 'flow_engine',
      flowId: currentFlowState.flowId,
      flowName: flowName,
      endType: 'stopped',
    };

    await updateDoc(contactRef, {
      [flowField]: null,
      [historyField]: updatedHistory,
      [`tags.${flowCompletedTagKey}`]: flowCompletedTag,
      updated_at: now,
      flow_sort_priority: 4,
    });

    // Clean up automation queue + any pre-prepared email
    dequeueAutomation(userId, contactId);
    clearPreparedEmail(userId, contactId);

    return true;
  } catch (error) {
    console.error('Error stopping flow:', error);
    return false;
  }
};

// Labels for condition types
const CONDITION_LABELS = {
  is_favorite: 'Is marked as Favorite?',
  email_opened: 'Has the email been opened?',
  email_clicked: 'Has the email been clicked?',
  replied: 'Did they reply?',
  score_above: 'Is the score above threshold?',
  has_phone: 'Do they have a phone number?',
  has_email: 'Do they have an email?',
  has_company: 'Do they have a company?',
  shown_interest: 'Has shown interest?',
  qualified: 'Is qualified?',
  decision_maker: 'Is decision maker?',
  has_budget: 'Has budget?',
  good_fit: 'Good fit?',
  ready_to_buy: 'Ready to buy?',
  needs_follow_up: 'Needs follow up?',
  meeting_went_well: 'Meeting went well?',
  responded_positively: 'Responded positively?',
  custom_question: 'Custom Question',
  meeting_booked: 'Meeting booked?',
  deal_closed: 'Deal closed?',
};

/**
 * Auto-evaluate a condition based on contact data.
 * Returns 'yes', 'no', or null (if the condition can't be auto-resolved).
 */
export const autoEvaluateCondition = (conditionType, contactData) => {
  if (!contactData) return null;
  switch (conditionType) {
    case 'is_favorite':
      return !!(contactData.tags && contactData.tags.favorite) ? 'yes' : 'no';
    case 'has_phone':
      return contactData.lead_telephone?.trim() ? 'yes' : 'no';
    case 'has_email':
      // Also counts decision-maker emails, not just the primary lead_email.
      return computeHasEmail(contactData) ? 'yes' : 'no';
    case 'has_company':
      return (contactData.business_name?.trim() || contactData.lead_company?.trim()) ? 'yes' : 'no';
    default:
      return null; // Can't auto-evaluate → requires manual decision
  }
};

/**
 * Get the current flow status for a contact
 * 
 * @param {object} contact - The contact data object
 * @param {array} flows - Available flows array
 * @param {string} flowType - 'warmup' or 'retargeting'
 * @returns {object|null} Flow status info or null
 */
export const getFlowStatus = (contact, flows, flowType = 'warmup') => {
  const flowField = FLOW_FIELD[flowType];
  const flowState = contact?.[flowField];
  
  if (!flowState || flowState.currentStepIndex < 0) return null;
  if (contact?.globalOptOut === true) return null;
  
  const flow = flows.find(f => f.id === flowState.flowId);
  if (!flow || !flow.steps) return null;
  
  const currentStep = flow.steps[flowState.currentStepIndex];
  if (!currentStep) return null;
  
  const now = Date.now();
  const stepDueTime = flowState.nextStepAvailableAt || flowState.startedAt;
  const isReady = now >= stepDueTime;
  const timeRemaining = Math.max(0, stepDueTime - now);
  
  // Check if it's a condition node
  // Support both nodeType === 'condition' and type === 'condition' for backwards compatibility
  const isConditionNode = currentStep.nodeType === 'condition' || currentStep.type === 'condition';
  
  // Manual conditions that require user decision
  const manualConditions = ['shown_interest', 'qualified', 'decision_maker', 'has_budget', 'good_fit', 'ready_to_buy', 'needs_follow_up', 'meeting_went_well', 'responded_positively', 'custom_question', 'meeting_booked', 'deal_closed'];
  const isManualCondition = isConditionNode && manualConditions.includes(currentStep.condition);
  
  // Check if condition can be auto-evaluated (e.g. is_favorite, has_phone, etc.)
  const isAutoEvaluable = isConditionNode && !isManualCondition && 
    autoEvaluateCondition(currentStep.condition, contact) !== null;
  
  // Get the condition label
  let conditionLabel = null;
  if (isConditionNode) {
    // Use custom question if available, otherwise use default label
    conditionLabel = currentStep.customQuestion || 
                     CONDITION_LABELS[currentStep.condition] || 
                     `${currentStep.condition}?`;
  }
  
  return {
    flow,
    flowState,
    currentStep,
    stepIndex: flowState.currentStepIndex,
    isReady,
    timeRemaining,
    isManualCondition, // Only manual conditions need user decision
    isConditionNode, // For display purposes
    isAutoEvaluable, // Can be auto-resolved without user input
    conditionLabel,
  };
};

/**
 * Format time remaining as readable string
 * 
 * @param {number} ms - Milliseconds remaining
 * @returns {string} Formatted time string
 */
export const formatTimeRemaining = (ms) => {
  if (ms <= 0) return 'Ready';
  
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Auto-advance a warmup flow when an email is sent to a contact.
 * Self-contained: reads contact doc + flow definition from Firebase directly.
 * Designed to be called fire-and-forget after a successful email send.
 *
 * @param {string} userId - The user ID
 * @param {string} contactId - The contact document ID
 * @returns {Promise<{advanced: boolean, flowName?: string, stepName?: string}>}
 */
export const advanceFlowOnEmailSend = async (userId, contactId) => {
  if (!userId || !contactId) return { advanced: false };

  try {
    const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
    const contactSnap = await getDoc(contactRef);
    if (!contactSnap.exists()) return { advanced: false };

    const contactData = contactSnap.data();

    // Try warmup first, then retargeting
    const candidates = [
      { flowState: contactData?.warmupFlow, flowType: 'warmup', defPath: 'warmupFlows' },
      { flowState: contactData?.activeFlow, flowType: 'retargeting', defPath: 'leadFlows' },
    ];

    for (const { flowState, flowType, defPath } of candidates) {
      if (!flowState || flowState.currentStepIndex < 0 || !flowState.flowId) continue;

      const flowRef = ref(database, `World/${userId}/settings/${defPath}/${flowState.flowId}`);
      const flowSnap = await get(flowRef);
      if (!flowSnap.exists()) continue;

      const flow = { id: flowState.flowId, ...flowSnap.val() };
      if (!flow.steps || flow.steps.length === 0) continue;

      const currentStep = flow.steps[flowState.currentStepIndex];
      if (!currentStep) continue;

      const stepType = currentStep.type || currentStep.nodeType;
      const isEmailStep = currentStep.nodeType === 'action' && EMAIL_ACTION_TYPES.includes(stepType);
      if (!isEmailStep) continue;

      const result = await completeFlowStep(
        userId, contactId, flow, flowState.currentStepIndex, flowType, null, { force: true }
      );

      if (result.success) {
        const stepLabel = stepType.replace(/_/g, ' ');
        console.log(`[Flow Auto-Advance] Flow "${flow.name}" (${flowType}) advanced past "${stepLabel}" for contact ${contactId}`);
        return {
          advanced: true,
          flowName: flow.name,
          stepName: stepLabel,
          newFlowState: result.newFlowState,
          flowEndStatus: result.flowEndStatus,
          autoAppliedTags: result.autoAppliedTags,
        };
      }
    }

    return { advanced: false };
  } catch (error) {
    console.error('[Flow Auto-Advance] Error:', error);
    return { advanced: false };
  }
};
